import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Clock, X, RotateCcw } from "lucide-react";
import { cardMotion, scrollReveal } from "../../home/motionConstants";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../ui/dialog";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../../ui/tooltip";
import useProcess from "../../../hooks/useProcess";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { config } from "../../../Environment";
import { toast } from "sonner";
import { DateTime } from "luxon";

interface ContinueStripProps {
  prescriptions: any[];
  isMobile: boolean;
  refreshLists?: () => void;
}

const ContinueStrip: React.FC<ContinueStripProps> = ({
  prescriptions,
  isMobile,
  refreshLists,
}) => {
  const { t, i18n } = useTranslation();
  const { resumeProcess } = useProcess();
  const retryFetch = useRetryFetch();
  const location = useLocation();

  const [chooserActivity, setChooserActivity] = useState<any>(null);
  const [terminatingId, setTerminatingId] = useState<string | null>(null);

  const runningActivities = useMemo(() => {
    return prescriptions
      .filter((p) => {
        if (!p.valid || !Array.isArray(p.processes)) return false;
        return p.processes.some((proc: any) => proc.status === "RUNNING");
      })
      .map((p) => {
        const runningProcesses = p.processes.filter(
          (proc: any) => proc.status === "RUNNING"
        );
        // Sort by most recently updated (lastUpdated is unix timestamp ms)
        const sorted = [...runningProcesses].sort(
          (a: any, b: any) =>
            (b.lastUpdated || b.started || 0) -
            (a.lastUpdated || a.started || 0)
        );
        return {
          _id: p._id,
          name: p.name,
          engagementCount: p.engagementCount || 0,
          runningCount: runningProcesses.length,
          runningProcesses: sorted,
          latestProcess: sorted[0],
          lastUpdatedTs: sorted[0]?.lastUpdated || sorted[0]?.started || 0,
          settings: p.settings,
        };
      })
      .sort(
        (a, b) => b.lastUpdatedTs - a.lastUpdatedTs
      );
  }, [prescriptions]);

  if (runningActivities.length === 0) return null;

  const handleResume = async (process: any) => {
    if (!process) return;

    let processId = process._id;
    if (process.pendingInnerProcess) {
      processId = process.pendingInnerProcess;
    }
    await resumeProcess({
      processId,
      from: location,
      forward: location,
      onError: () => {
        toast.error(
          t("error_continuing_process") ||
            "An error occurred while continuing the process. Please try again."
        );
      },
    });
  };

  const handleContinueClick = (activity: any) => {
    if (activity.runningCount > 1) {
      setChooserActivity(activity);
    } else {
      handleResume(activity.latestProcess);
    }
  };

  const handleTerminate = async (processId: string) => {
    setTerminatingId(processId);
    try {
      const response = await retryFetch(
        `${config.API_BASE}/api/processes/${processId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      const msg = await response.json();
      toast.success(t(msg.message) || t("engagement_terminated") || "Engagement terminated");

      // Update chooser state — remove the terminated process
      if (chooserActivity) {
        const remaining = chooserActivity.runningProcesses.filter(
          (p: any) => p._id !== processId
        );
        if (remaining.length === 0) {
          setChooserActivity(null);
        } else {
          setChooserActivity({
            ...chooserActivity,
            runningProcesses: remaining,
            runningCount: remaining.length,
          });
        }
      }

      refreshLists?.();
    } catch {
      toast.error(
        t("error_terminating_process") || "Failed to terminate engagement"
      );
    } finally {
      setTerminatingId(null);
    }
  };

  const displayedActivities = runningActivities.slice(0, isMobile ? 5 : 10);

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <motion.div {...scrollReveal} className="bg-gradient-to-r from-emerald-50/70 via-green-50/40 to-teal-50/30 border border-emerald-200/40 rounded-2xl p-3.5 sm:p-4 mb-4 sm:mb-5 shadow-[0_1px_4px_rgba(16,185,129,0.06)]">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-6 h-6 rounded-lg bg-emerald-500/12 flex items-center justify-center">
              <RotateCcw className="w-3 h-3 text-emerald-600" />
            </div>
            <h3 className="text-sm font-semibold text-emerald-900 tracking-[-0.01em]">
              {t("continue_where_you_left_off")}
            </h3>
            <span className="text-[11px] font-medium text-emerald-600/70 bg-emerald-100/60 px-2 py-0.5 rounded-full">
              {t("activities_count", { count: displayedActivities.length }) || `${displayedActivities.length} activities`}
            </span>
          </div>
          <div className="flex gap-2.5 overflow-x-auto no-scrollbar pb-0.5">
            {displayedActivities.map((activity) => (
              <Tooltip key={activity._id}>
                <TooltipTrigger asChild>
                  <motion.button
                    {...cardMotion}
                    onClick={() => handleContinueClick(activity)}
                    className="flex items-center gap-2.5 bg-white/80 backdrop-blur-sm rounded-xl px-3.5 py-2.5 text-start hover:bg-white hover:shadow-md transition-shadow duration-200 group flex-shrink-0 max-w-[280px] border border-white/60 hover:border-emerald-200/50"
                  >
                    <div className="w-7 h-7 rounded-lg bg-emerald-100/80 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200/80 transition-colors">
                      <Play className="w-3 h-3 text-emerald-600 fill-emerald-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-gray-900 truncate leading-snug">
                        {activity.name}
                      </p>
                      <div className="flex items-center gap-2">
                        {activity.lastUpdatedTs > 0 && (
                          <span className="text-[11px] text-gray-400 flex items-center gap-1">
                            <Clock className="w-2.5 h-2.5" />
                            {DateTime.fromMillis(activity.lastUpdatedTs)
                              .setLocale(i18n.language)
                              .toRelative({ locale: i18n.language })}
                          </span>
                        )}
                        {activity.runningCount > 1 && (
                          <span className="text-[11px] font-medium text-emerald-600">
                            {activity.runningCount} {t("in_progress")}
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{activity.name}</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </motion.div>
      </TooltipProvider>

      {/* Engagement chooser modal — shown when multiple running engagements */}
      <Dialog
        open={!!chooserActivity}
        onOpenChange={(open) => {
          if (!open) setChooserActivity(null);
        }}
      >
        <DialogContent className="max-w-md gap-3">
          <DialogHeader>
            <DialogTitle className="text-base">
              {chooserActivity?.name}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {t("choose_engagement_to_resume") ||
                "Choose which engagement to continue"}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
            {chooserActivity?.runningProcesses.map((process: any) => {
              const ts = process.lastUpdated || process.started;
              return (
                <div
                  key={process._id}
                  className="flex items-center gap-3 border rounded-lg px-3 py-2.5 hover:border-green-300 hover:bg-green-50/30 transition-all group/row"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {process.engagementName || t("untitled_engagement") || "Untitled"}
                    </p>
                    {ts && (
                      <span className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" />
                        {DateTime.fromMillis(ts)
                          .setLocale(i18n.language)
                          .toRelative({ locale: i18n.language })}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      onClick={() => handleResume(process)}
                      className="bg-green-600 hover:bg-green-700 text-white h-7 px-3 text-xs rounded-md"
                    >
                      <Play className="w-3 h-3 me-1 fill-white" />
                      {t("resume")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleTerminate(process._id)}
                      disabled={terminatingId === process._id}
                      className="h-7 w-7 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 sm:opacity-0 sm:group-hover/row:opacity-100 transition-all"
                      title={t("terminate") || "Terminate"}
                    >
                      {terminatingId === process._id ? (
                        <span className="w-3 h-3 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                      ) : (
                        <X className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContinueStrip;
